import { merge } from 'lodash';
import { BBModel, BBSymbolInfo, CardData, StorySymbols } from 'types/story';
import { isSymbol } from 'utils/symbol';

/**
 * Walk components depth-first and call a callback on each
 *
 * @function componentWalk
 * @param {!Array}   components  - Array of components
 * @param {callback} cb          - callback to run on each components
 * @param {string}   [path]      - dot-notated path to component in components param
 * @example
 *      const arr: BBSymbolInstance[] = [];
 *
 *      componentWalk<BBSymbolInstance>(arr, function step({ component }) {
 *          componentWalk<BBSymbolInstance>(component.children, step);
 *      });
 *
 * @return void
 */

export const componentWalk = <T = BBModel>(
	components: T[] | BBModel['children'],
	cb: (props: { component: T; index: number; path: string }) => void,
	path: string = ''
) => {
	if (!Array.isArray(components)) {
		return;
	}
	components.forEach(
		(component, index) =>
			component &&
			cb({
				component,
				index,
				path: path === '' ? `${index}` : `${path}.children.${index}`,
			})
	);
};

/**
 * @callback callback
 * @param {CallbackProps} props
 */

/**
 * @typedef CallbackProps
 * @property {object} component - component object model
 * @property {number} index     - component index among parent children
 * @property {string} path      - dot-notated path to component from components root array
 */

/**
 * Walk components depth-first and call a callback on each,
 * Every component, even if it's is symbol, will be represented as fully merged with master & instance
 */
export function componentWalkFull({
	elements,
	symbols,
	callback,
}: {
	elements: CardData['elements'];
	symbols: StorySymbols;
	callback: (p: {
		component: BBModel;
		index: number;
		path: string;
		/**
		 * Stop component walk
		 */
		stop: () => void;
	}) => void;
}) {
	let complete = false;

	function stop() {
		complete = true;
	}

	componentWalk<BBModel>(elements, function walk({ component, index, path }) {
		if (isSymbol(component) && symbols && !component._id) {
			const { masterId, instanceId } = component.symbol as BBSymbolInfo;
			const instance = symbols[masterId].instance[instanceId];
			const { master } = symbols[masterId];
			const fullComponent = merge({}, master, instance);
			componentWalk<BBModel>([fullComponent], walk);
			return;
		}

		callback({ component, index, path, stop });

		if (!complete) componentWalk<BBModel>(component.children, walk);
	});
}
